import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X, Calendar as CalendarIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { MoneyInput } from "@/components/ui/money-input";
import { extractDatePart, extractTimePart, sanitizeDatetimeLocal } from "@/lib/datetimeLocal";
import { openNativeDateTimePicker } from "@/lib/dateTimePicker";
import { PriceWithAdjustment } from "@/components/proposals/PriceWithAdjustment";

const formatToTimestamp = (datetimeLocal: string): string => {
  if (!datetimeLocal) return "";
  return sanitizeDatetimeLocal(datetimeLocal);
};

const formatToDatetimeLocal = (timestamp: string): string => {
  if (!timestamp) return "";
  const datePart = extractDatePart(timestamp);
  const timePart = extractTimePart(timestamp);
  if (!datePart || !timePart) return "";
  return `${datePart}T${timePart}`;
};

interface FlightsStepProps {
  data: any;
  onChange: (data: any) => void;
  readOnly?: boolean;
}

const FlightsStep = ({ data, onChange, readOnly }: FlightsStepProps) => {
  const isReadOnly = !!readOnly;
  const [flights, setFlights] = useState<any[]>(data.flights || []);
  const inputClassName = "h-11";
  const dateTimeInputClassName = "w-[280px] max-w-full";

  const initialFlight = {
    airline: "",
    flight_number: "",
    flight_class: "",
    departure_airport: "",
    arrival_airport: "",
    departure_datetime: "",
    arrival_datetime: "",
    booking_with_us: false,
    booking_price: null as number | null,
  };

  useEffect(() => {
    if (!Array.isArray(data.flights)) {
      setFlights([]);
      return;
    }
    setFlights(data.flights || []);
  }, [data.flights]);

  const addFlight = () => {
    if (isReadOnly) return;
    const newFlights = [
      { ...initialFlight },
      ...flights,
    ];
    setFlights(newFlights);
    onChange({ ...data, flights: newFlights });
  };

  const removeFlight = (index: number) => {
    if (isReadOnly) return;
    const newFlights = flights.filter((_, i) => i !== index);
    setFlights(newFlights);
    onChange({ ...data, flights: newFlights });
  };

  const updateFlight = (index: number, field: string, value: any) => {
    if (isReadOnly) return;
    const newFlights = [...flights];
    if (field === "departure_datetime" || field === "arrival_datetime") {
      const sanitized = sanitizeDatetimeLocal(value);
      newFlights[index] = { ...newFlights[index], [field]: formatToTimestamp(sanitized) };
    } else if (field === "booking_with_us") {
      const enabled = Boolean(value);
      newFlights[index] = {
        ...newFlights[index],
        booking_with_us: enabled,
        booking_price: enabled ? (newFlights[index]?.booking_price ?? null) : null,
      };
    } else {
      newFlights[index] = { ...newFlights[index], [field]: value };
    }
    setFlights(newFlights);
    onChange({ ...data, flights: newFlights });
  };

  const parseMoneyValue = (value: string): number | null => {
    if (value.trim() === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Flights</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add flight information for the trip
          </p>
        </div>
        <Button type="button" size="sm" onClick={addFlight} disabled={isReadOnly}>
          <Plus className="mr-2 h-4 w-4" />
          Add Flight
        </Button>
      </div>

      {flights.length === 0 ? (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center ${isReadOnly ? "" : "cursor-pointer transition-colors hover:bg-muted/30"}`}
          onClick={isReadOnly ? undefined : addFlight}
          onKeyDown={isReadOnly ? undefined : (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              addFlight();
            }
          }}
          role={isReadOnly ? undefined : "button"}
          tabIndex={isReadOnly ? -1 : 0}
          aria-label={isReadOnly ? undefined : "Add flight"}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">No flights added yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Add Flight" to get started
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {flights.map((flight, index) => (
            <div key={index} className="grid gap-5 p-6 border rounded-lg bg-card relative shadow-sm hover:shadow-md transition-shadow">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-3 right-3 h-8 w-8 p-0"
                onClick={() => removeFlight(index)}
                disabled={isReadOnly}
              >
                <X className="h-4 w-4" />
              </Button>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Airline</Label>
                  <Input
                    value={flight.airline || ""}
                    onChange={(e) => updateFlight(index, "airline", e.target.value)}
                    placeholder="e.g., Emirates"
                    className={inputClassName}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Flight Number</Label>
                  <Input
                    value={flight.flight_number || ""}
                    onChange={(e) => updateFlight(index, "flight_number", e.target.value)}
                    placeholder="e.g., EK215"
                    className={inputClassName}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Flight Class</Label>
                  <Input
                    value={flight.flight_class || ""}
                    onChange={(e) => updateFlight(index, "flight_class", e.target.value)}
                    placeholder="e.g., Economy"
                    className={inputClassName}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Departure Airport</Label>
                  <Input
                    value={flight.departure_airport || ""}
                    onChange={(e) => updateFlight(index, "departure_airport", e.target.value)}
                    placeholder="e.g., DXB"
                    className={inputClassName}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Arrival Airport</Label>
                  <Input
                    value={flight.arrival_airport || ""}
                    onChange={(e) => updateFlight(index, "arrival_airport", e.target.value)}
                    placeholder="e.g., JFK"
                    className={inputClassName}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium">
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      Departure Date & Time
                    </Label>
                    <Input
                      type="datetime-local"
                      value={formatToDatetimeLocal(flight.departure_datetime || "")}
                      onChange={(e) => updateFlight(index, "departure_datetime", e.target.value)}
                      onClick={(e) => openNativeDateTimePicker(e.currentTarget)}
                      className={`${inputClassName} ${dateTimeInputClassName}`}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-sm font-medium">
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      Arrival Date & Time
                    </Label>
                    <Input
                      type="datetime-local"
                      value={formatToDatetimeLocal(flight.arrival_datetime || "")}
                      onChange={(e) => updateFlight(index, "arrival_datetime", e.target.value)}
                      onClick={(e) => openNativeDateTimePicker(e.currentTarget)}
                      className={`${inputClassName} ${dateTimeInputClassName}`}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={!!flight.booking_with_us}
                    onCheckedChange={(checked) => updateFlight(index, "booking_with_us", Boolean(checked))}
                    disabled={isReadOnly}
                  />
                  <Label className="text-sm font-normal cursor-pointer">Booking With Us</Label>
                </div>
                {flight.booking_with_us && (
                  <div className="mt-4 space-y-2">
                    <Label className="text-sm font-medium">Booking Price</Label>
                    <MoneyInput
                      value={flight.booking_price}
                      onChange={(value) => updateFlight(index, "booking_price", parseMoneyValue(value))}
                      placeholder="Enter booking price"
                      className="w-full max-w-sm"
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end border-t pt-3">
                <div className="text-right">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Item total</p>
                  <PriceWithAdjustment
                    baseAmount={flight.booking_with_us ? Number(flight.booking_price || 0) : 0}
                    proposalData={data}
                    emptyLabel={!flight.booking_with_us ? "Booked directly" : undefined}
                    size="md"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FlightsStep;
